import React, { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import toast from 'react-hot-toast'
import { GalleryItem, UpdateGalleryItemData, galleryApi } from '../services/galleryApi'
import { eventsApi } from '../services/eventsApi'
import { usersApi } from '../services/usersApi'
import { locationsApi } from '../services/locationsApi'

interface EditPhotoModalProps {
  isOpen: boolean
  onClose: () => void
  photo: GalleryItem | null
  onPhotoUpdated: (updatedPhoto: GalleryItem) => void
}

interface TagOptions {
  events: Array<{ id: string; name: string }>
  members: Array<{ id: string; name: string }>
  locations: Array<{ id: string; name: string }>
}

export default function EditPhotoModal({
  isOpen,
  onClose,
  photo,
  onPhotoUpdated
}: EditPhotoModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingTags, setIsLoadingTags] = useState(false)
  
  const [formData, setFormData] = useState<UpdateGalleryItemData>({
    title: '',
    description: '',
    tags: {
      events: [],
      years: [],
      members: [],
      locations: [],
      scheduledEvents: []
    }
  })
  
  const [tagOptions, setTagOptions] = useState<TagOptions>({
    events: [],
    members: [],
    locations: []
  })

  // Load tag options
  useEffect(() => {
    const loadTagOptions = async () => {
      if (!isOpen) return
      
      setIsLoadingTags(true)
      try {
        // Initialize APIs with auth
        eventsApi.setAuth(getAccessTokenSilently)
        usersApi.setAuth(getAccessTokenSilently)
        locationsApi.setAuth(getAccessTokenSilently)

        const [eventsData, membersData, locationsData] = await Promise.all([
          eventsApi.getEvents(),
          usersApi.getUsers(),
          locationsApi.getLocations()
        ])

        setTagOptions({
          events: eventsData.events.map(event => ({ id: event.eventId, name: event.title })),
          members: membersData.users.map(member => ({ 
            id: member.userId, 
            name: member.name || `${member.firstName} ${member.lastName}`.trim() 
          })),
          locations: locationsData.locations.map(location => ({ id: location.locationId, name: location.name }))
        })
      } catch (error) {
        console.error('Error loading tag options:', error)
        toast.error('Failed to load tag options')
      } finally {
        setIsLoadingTags(false)
      }
    }

    loadTagOptions()
  }, [isOpen, getAccessTokenSilently])

  // Update form data when photo changes
  useEffect(() => {
    if (photo) {
      setFormData({
        title: photo.title || '',
        description: photo.description || '',
        tags: {
          events: photo.tags?.events || [],
          years: photo.tags?.years || [],
          members: photo.tags?.members || [],
          locations: photo.tags?.locations || [],
          scheduledEvents: photo.tags?.scheduledEvents || []
        }
      })
    }
  }, [photo])

  const handleTagToggle = (tagType: keyof UpdateGalleryItemData['tags'], tagValue: string | number) => {
    setFormData(prev => ({
      ...prev,
      tags: {
        ...prev.tags,
        [tagType]: prev.tags![tagType]!.includes(tagValue as never)
          ? prev.tags![tagType]!.filter((v: any) => v !== tagValue)
          : [...prev.tags![tagType]!, tagValue as never]
      }
    }))
  }

  const handleYearChange = (year: string) => {
    const yearNum = parseInt(year)
    if (isNaN(yearNum)) return
    
    setFormData(prev => ({
      ...prev,
      tags: {
        ...prev.tags,
        years: prev.tags!.years!.includes(yearNum)
          ? prev.tags!.years!.filter(y => y !== yearNum)
          : [...prev.tags!.years!, yearNum]
      }
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!photo) return

    if (!formData.title?.trim()) {
      toast.error('Title is required')
      return
    }

    try {
      setIsSubmitting(true)
      
      const updatedPhoto = await galleryApi.updateItem(photo.id, formData, getAccessTokenSilently)
      
      toast.success('Photo updated successfully!')
      onPhotoUpdated(updatedPhoto)
      onClose()
    } catch (error) {
      console.error('Error updating photo:', error)
      toast.error('Failed to update photo')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen || !photo) return null

  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => currentYear - i)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-2xl p-6 my-8 text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Edit Photo
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo Preview */}
            <div className="flex justify-center">
              <img
                src={photo.src}
                alt={photo.title}
                className="max-w-full max-h-48 object-contain rounded-lg"
              />
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Tags */}
            {isLoadingTags ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading tags...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Events */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Events
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {tagOptions.events.map(event => (
                      <label key={event.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.tags?.events?.includes(event.id) || false}
                          onChange={() => handleTagToggle('events', event.id)}
                          className="mr-2 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{event.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Locations */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Locations
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {tagOptions.locations.map(location => (
                      <label key={location.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.tags?.locations?.includes(location.id) || false}
                          onChange={() => handleTagToggle('locations', location.id)}
                          className="mr-2 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{location.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Members */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Members
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {tagOptions.members.map(member => (
                      <label key={member.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.tags?.members?.includes(member.id) || false}
                          onChange={() => handleTagToggle('members', member.id)}
                          className="mr-2 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{member.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Years */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Years
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {yearOptions.map(year => (
                      <label key={year} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.tags?.years?.includes(year) || false}
                          onChange={() => handleYearChange(year.toString())}
                          className="mr-2 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{year}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Updating...' : 'Update Photo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
